import { useCallback, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { cn } from '@/shared/utils';
import { cva } from 'class-variance-authority';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  type DialogComponentOptions,
  type DialogOptions,
  useModalStore,
} from '@/shared/lib/modalStore';
import { ConfirmationModal } from '@/widgets/confirmation-modal';

const dialogContentVariants = cva('rounded-[2.4rem]', {
  variants: {
    type: {
      component: 'max-h-[90vh] overflow-y-auto',
      standard: 'p-[2rem]',
    },
  },
  defaultVariants: {
    type: 'standard',
  },
});

export function DialogProvider() {
  const {
    isOpen,
    dialogConfig,
    setDialogConfig,
    closeDialog,
    isCloseConfirmationRequired,
  } = useModalStore();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const clearDialogConfig = useCallback(() => {
    setDialogConfig(null);
    setIsConfirmationOpen(false);
  }, [setDialogConfig]);

  // Handle animation end to clear content when slide-out animation completes
  const handleAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLDivElement>) => {
      // Only clear content if it's the slide-out animation and dialog is closed
      if (event.animationName.includes('slide-out') && !isOpen) {
        clearDialogConfig();
      }
    },
    [isOpen, clearDialogConfig]
  );

  const handleConfirm = () => {
    if (dialogConfig && 'onConfirm' in dialogConfig) {
      dialogConfig.onConfirm?.();
    }
    closeDialog();
  };

  const handleCancel = () => {
    if (dialogConfig && 'onCancel' in dialogConfig) {
      dialogConfig.onCancel?.();
    } else if (dialogConfig && 'onClose' in dialogConfig) {
      dialogConfig.onClose?.();
    }
    closeDialog();
  };

  const attemptClose = useCallback(() => {
    const config = dialogConfig?.closeConfirmation;
    const shouldShowConfirmation = config && isCloseConfirmationRequired;

    if (shouldShowConfirmation) {
      setIsConfirmationOpen(true);
    } else {
      handleCancel();
    }
  }, [dialogConfig, isCloseConfirmationRequired, handleCancel]);

  const handleConfirmationConfirm = useCallback(() => {
    handleCancel();
    setIsConfirmationOpen(false);
  }, [handleCancel]);

  const handleConfirmationCancel = useCallback(() => {
    setIsConfirmationOpen(false);
  }, []);

  const handleCloseButtonClick = useCallback(() => {
    if (dialogConfig && 'component' in dialogConfig) {
      const componentConfig = dialogConfig as DialogComponentOptions;
      if (componentConfig.onClose) {
        const config = componentConfig.closeConfirmation;
        const shouldShowConfirmation = config && isCloseConfirmationRequired;

        if (shouldShowConfirmation) {
          setIsConfirmationOpen(true);
        } else {
          componentConfig.onClose();
        }
      } else {
        attemptClose();
      }
    } else {
      attemptClose();
    }
  }, [dialogConfig, isCloseConfirmationRequired, attemptClose]);

  const handleOpenChange = (open: boolean) => {
    if (
      dialogConfig &&
      'preventOverlayClose' in dialogConfig &&
      dialogConfig.preventOverlayClose
    ) {
      return;
    }

    if (!open) {
      attemptClose();
    }
  };

  const isComponentDialog = dialogConfig && 'component' in dialogConfig;
  const confirmationConfig = dialogConfig?.closeConfirmation;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          ref={dialogRef}
          onAnimationEnd={handleAnimationEnd}
          className={cn(
            dialogContentVariants({
              type: isComponentDialog ? 'component' : 'standard',
            }),
            dialogConfig?.className
          )}
          showCloseButton={
            (dialogConfig as DialogComponentOptions)?.showCloseButton
          }
          customCloseButton={
            isComponentDialog
              ? (dialogConfig as DialogComponentOptions).customCloseButton
              : undefined
          }
          onClose={isComponentDialog ? handleCloseButtonClick : undefined}>
          {dialogConfig && (
            <>
              {isComponentDialog ? (
                (dialogConfig as DialogComponentOptions).component
              ) : (
                <>
                  <DialogHeader>
                    {(dialogConfig as DialogOptions).title ? (
                      <DialogTitle className="headline1">
                        {(dialogConfig as DialogOptions).title}
                      </DialogTitle>
                    ) : (
                      <VisuallyHidden>
                        <DialogTitle>Dialog</DialogTitle>
                      </VisuallyHidden>
                    )}
                    {(dialogConfig as DialogOptions).description && (
                      <DialogDescription className="p3">
                        {(dialogConfig as DialogOptions).description}
                      </DialogDescription>
                    )}
                  </DialogHeader>
                  {(dialogConfig as DialogOptions).showFooter !== false && (
                    <DialogFooter>
                      <Button
                        className="p3"
                        variant="outline"
                        onClick={handleCancel}>
                        {(dialogConfig as DialogOptions).cancelText || 'Cancel'}
                      </Button>
                      <Button className="p3" onClick={handleConfirm}>
                        {(dialogConfig as DialogOptions).confirmText ||
                          'Confirm'}
                      </Button>
                    </DialogFooter>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {confirmationConfig && (
        <ConfirmationModal
          isOpen={isConfirmationOpen}
          onClose={handleConfirmationCancel}
          onConfirm={handleConfirmationConfirm}
          title={confirmationConfig.title}
          description={confirmationConfig.description}
          confirmText={confirmationConfig.confirmText}
          cancelText={confirmationConfig.cancelText}
          confirmVariant={confirmationConfig.confirmVariant}
          showCancelButton={confirmationConfig.showCancelButton}
        />
      )}
    </>
  );
}
